import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { isAnswerableQuestionType, type TSubmitFormAnswer } from '@onesource/schemas';
import {
  useSaveReviewDraft,
  useSubmitReviewFeedback,
  type TFormWithQuestions,
  type TReviewAnswer,
} from '@onesource/services';
import { enqueueSnackbar } from 'notistack';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdSend } from 'react-icons/md';

import { QuestionBlock } from '../form/question-block';
import { DraftStatus } from './draft-status';
import { emptyAnswer, hasAnswerValue, isRequiredAnswered, toAnswerMap } from './review-answer-utils';

const AUTOSAVE_DELAY_MS = 1500;

interface FeedbackEditorProps {
  spaceId: string;
  submissionId: string;
  feedbackForm: TFormWithQuestions;
  initialAnswers: TReviewAnswer[];
  /** Already delivered to the student — then it is read-only. */
  isDelivered: boolean;
  /** A draft is already saved on the server. */
  hasDraft: boolean;
  canEdit: boolean;
}

/**
 * The teacher's feedback, filled in with the linked Feedback form. Autosaves as a
 * draft until the teacher sends it; the student is notified then. Sent feedback is
 * final and shows read-only.
 */
export const FeedbackEditor: FC<FeedbackEditorProps> = ({
  spaceId,
  submissionId,
  feedbackForm,
  initialAnswers,
  isDelivered,
  hasDraft,
  canEdit,
}) => {
  const [answers, setAnswers] = useState<Record<string, TSubmitFormAnswer>>(() => toAnswerMap(initialAnswers));
  const [isDirty, setIsDirty] = useState(false);
  const saveDraft = useSaveReviewDraft();
  const submitFeedback = useSubmitReviewFeedback();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const isEditable = canEdit && !isDelivered;

  const questions = useMemo(
    () => [...feedbackForm.questions].sort((a, b) => a.position - b.position),
    [feedbackForm.questions],
  );
  const missingRequired = questions.filter(
    q => q.isRequired && isAnswerableQuestionType(q.questionType) && !isRequiredAnswered(q, answers[q.id]),
  );
  const filledAnswers = useCallback(() => Object.values(answers).filter(a => hasAnswerValue(a)), [answers]);

  const persistDraft = useCallback(
    (draftAnswers: TSubmitFormAnswer[]) =>
      saveDraft.mutate(
        { spaceId, submissionId, answers: draftAnswers },
        { onSuccess: () => setLastSavedAt(new Date()) },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spaceId, submissionId],
  );

  // Draft autosave. A change still waiting for the timer is saved when the teacher leaves the page.
  const pendingDraftRef = useRef<TSubmitFormAnswer[] | null>(null);
  useEffect(() => {
    if (!isDirty || !isEditable) {
      return undefined;
    }
    pendingDraftRef.current = filledAnswers();
    timerRef.current = setTimeout(() => {
      if (pendingDraftRef.current) {
        persistDraft(pendingDraftRef.current);
        pendingDraftRef.current = null;
      }
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [answers, isDirty, isEditable, filledAnswers, persistDraft]);

  useEffect(
    () => () => {
      if (pendingDraftRef.current) {
        persistDraft(pendingDraftRef.current);
      }
    },
    [persistDraft],
  );

  const handleChange = useCallback((answer: TSubmitFormAnswer) => {
    setAnswers(prev => ({ ...prev, [answer.questionId]: answer }));
    setIsDirty(true);
  }, []);

  const handleSubmit = async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    pendingDraftRef.current = null;
    try {
      await submitFeedback.mutateAsync({ spaceId, submissionId, answers: filledAnswers() });
      setIsDirty(false);
      enqueueSnackbar('Feedback sent to the student', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar((error as { message?: string })?.message ?? 'Could not send feedback', { variant: 'error' });
    }
  };

  return (
    <Box>
      {isDelivered ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This feedback was sent to the student. It can no longer be changed.
        </Alert>
      ) : (
        !canEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only the student&apos;s assigned teacher can write this feedback.
          </Alert>
        )
      )}
      {isEditable && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your feedback saves as a draft while you write. The student sees it only after you click Send feedback.
        </Typography>
      )}
      {questions.map(question => (
        <QuestionBlock
          key={question.id}
          question={question}
          answer={answers[question.id] ?? emptyAnswer(question.id)}
          onAnswerChange={handleChange}
          disabled={!isEditable || submitFeedback.isPending}
          compact
        />
      ))}
      {isEditable && (
        // Sticks to the bottom of the screen, so Send stays in reach in a long feedback form.
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            flexWrap: 'wrap',
            // Run edge to edge over the card's padding.
            mx: { xs: -2, md: -3 },
            mb: -2.5,
            px: { xs: 2, md: 3 },
            py: 1.5,
            bgcolor: 'common.white',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.25}>
            <DraftStatus
              isSaving={saveDraft.isPending}
              isError={saveDraft.isError}
              lastSavedAt={lastSavedAt}
              hasDraft={hasDraft}
            />
            <Typography variant="caption" color="text.secondary">
              {missingRequired.length > 0 ? `${missingRequired.length} required question(s) left` : ''}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={submitFeedback.isPending ? <CircularProgress size={16} color="inherit" /> : <MdSend />}
            disabled={submitFeedback.isPending || missingRequired.length > 0}
            onClick={handleSubmit}
          >
            Send feedback
          </Button>
        </Box>
      )}
    </Box>
  );
};
